<template>
    <b-card
            class="service-provider-card"
            :class="{
                'is-selectable': selectable,
                'is-selected': selected,
                'detailed': variant === 'detailed',
            }"
            :header-class="{
                'd-none': !selectable,
            }"
            @click="handleClick"
    >
        <template #header>
            <b-form-radio
                    v-if="selectable && selectType === SELECT_TYPES.RADIO"
                    v-show="selected"
                    :value="serviceProvider"
                    :checked="serviceProvider"
                    class="provider-radio-button"
                    @click.prevent=""
            />
            <b-form-checkbox
                    v-if="selectable && selectType === SELECT_TYPES.CHECKBOX"
                    :checked="selected"
                    class="provider-radio-button"
            />
        </template>


        <div class="provider-avatar">
            <avatar
                    v-if="avatar.type === 'avatar'"
                    :src="avatar.src"
                    :name="avatar.name"
                    :size="avatarSize"
                    :class="avatar.class"
            />

            <i
                    v-if="avatar.type === 'icon'"
                    class="far fa-building text-gray-700 cursor-pointer"
            />
        </div>

        <div
                class="provider-name"
                :class="{
                    'mb-0': serviceProvider.hasContact,
                }"
        >
            {{ name }}
        </div>

        <div
                v-if="serviceProvider.hasContact && Boolean(subtitle)"
                class="provider-subtitle"
        >
            {{ subtitle }}
        </div>

        <template v-if="variant === 'simple'">
            <div
                    v-if="hasDescription"
                    class="provider-description"
            >
                {{ serviceProvider.description }}
            </div>
        </template>

        <template v-if="variant === 'simple-phone-website'">
            <service-provider-card-phone :service-provider="serviceProvider" />
            <service-provider-card-website :service-provider="serviceProvider" />
            <div
                    v-if="hasDescription"
                    class="provider-description"
            >
                {{ serviceProvider.description }}
            </div>
        </template>

        <template v-if="variant === 'detailed'">
            <div
                    v-if="serviceProvider.hasContact && serviceProvider.contactPhone"
                    class="provider-phone"
            >
                {{ serviceProvider.contactPhone }}
            </div>

            <div
                    v-if="serviceProvider.hasContact && serviceProvider.contactWebsite"
                    class="provider-website"
            >
                <b-link
                        :href="addProtocol(serviceProvider.contactWebsite)"
                        target="_blank"
                >
                    {{ shortenUrl(serviceProvider.contactWebsite) }}
                </b-link>
            </div>

            <div
                    v-if="displayOrganizationLogo"
                    class="organization-logo"
            >
                <avatar
                        :src="organizationLogo.src"
                        :name="organizationLogo.name"
                        :size="avatarSize"
                        class="organization-logo-avatar"
                />
            </div>

            <div
                    v-if="displayOrganizationName"
                    class="organization-name"
            >
                {{ serviceProvider.name }}
            </div>

            <div
                    v-if="serviceProvider.phone"
                    class="organization-phone"
            >
                {{ serviceProvider.phone }}
            </div>

            <div
                    v-if="serviceProvider.website"
                    class="organization-website"
            >
                <b-link
                        :href="addProtocol(serviceProvider.website)"
                        target="_blank"
                >
                    {{ shortenUrl(serviceProvider.website) }}
                </b-link>
            </div>
        </template>
    </b-card>
</template>

<script>
    import './ServiceProviderCard.scss';
    import ServiceProviderAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/ServiceProviderAppResource";
    import VModel from "../../../mixins/VModel";
    import isEmpty from 'lodash/isEmpty';
    import isString from 'lodash/isString';
    import set from 'lodash/set';
    import IsMobileMixin from "../../../mixins/IsMobileMixin";
    import {addProtocol, shortenUrl} from "../../../helpers/url";
    import ServiceProviderCardPhone from './ServiceProviderCardPhone/ServiceProviderCardPhone.vue';
    import ServiceProviderCardWebsite from './ServiceProviderCardWebsite/ServiceProviderCardWebsite.vue';

    export const RADIO = 'radio';
    export const CHECKBOX = 'checkbox';
    export const SELECT_TYPES = {
        RADIO,
        CHECKBOX,
    };

    export default {
        name: "ServiceProviderCard",
        components: {
            ServiceProviderCardPhone,
            ServiceProviderCardWebsite,
        },
        mixins: [
            VModel,
            IsMobileMixin,
        ],

        props: {
            serviceProvider: {
                type: ServiceProviderAppResource,
                required: true,
            },
            selectable: {
                type: Boolean,
                default: false,
            },
            selected: {
                type: Boolean,
                default: false,
            },
            variant: {
                type: String,
                default: 'simple',
                validator(variant) {
                    return ['simple', 'simple-phone-website', 'detailed'].includes(variant);
                }
            },
            selectType: {
                type: String,
                default: RADIO,
                validator(variant) {
                    return Object.values(SELECT_TYPES).includes(variant);
                }
            }
        },

        data() {
            return {
                SELECT_TYPES
            };
        },
        computed: {
            name() {
                if (this.serviceProvider.hasContact) {
                    return `${this.serviceProvider.contactFirstName} ${this.serviceProvider.contactLastName}`;
                }

                return this.serviceProvider.name;
            },

            avatar() {
                if (this.serviceProvider.hasContact) {
                    const config = {
                        type: 'avatar',
                        name: `${this.serviceProvider.contactFirstName} ${this.serviceProvider.contactLastName}`,
                        class: 'contact-avatar'
                    };

                    if (
                        !isEmpty(this.serviceProvider.contactAvatar) &&
                        !isEmpty(this.serviceProvider.contactAvatar.publicStorageUrl)
                    ) {
                        set(config, 'src', this.serviceProvider.contactAvatar.publicStorageUrl);
                    }

                    if (
                        isEmpty(this.serviceProvider.contactAvatar) ||
                        isEmpty(this.serviceProvider.contactAvatar.publicStorageUrl)
                    ) {
                        if (
                            !isEmpty(this.serviceProvider.organizationLogo) &&
                            !isEmpty(this.serviceProvider.organizationLogo.publicStorageUrl)
                        ) {
                            return {
                                type: 'avatar',
                                src: this.serviceProvider.organizationLogo.publicStorageUrl,
                                class: 'organization-logo-avatar'
                            };
                        }
                    }

                    return config;
                }

                if (
                    !isEmpty(this.serviceProvider.organizationLogo) &&
                    !isEmpty(this.serviceProvider.organizationLogo.publicStorageUrl)
                ) {
                    return {
                        type: 'avatar',
                        src: this.serviceProvider.organizationLogo.publicStorageUrl,
                        class: 'organization-logo-avatar'
                    };
                }
                if (
                    !isEmpty(this.serviceProvider.organizationLogo) &&
                    !isEmpty(this.serviceProvider.organizationLogo.publicUrl)
                ) {
                    return {
                        type: 'avatar',
                        src: this.serviceProvider.organizationLogo.publicUrl,
                        class: 'organization-logo-avatar'
                    };
                }

                if (
                    !isEmpty(this.serviceProvider.contactAvatar) &&
                    !isEmpty(this.serviceProvider.contactAvatar.publicUrl)
                ) {
                    return {
                        type: 'avatar',
                        class: 'contact-avatar',
                        src: this.serviceProvider.contactAvatar.publicUrl
                    };
                }

                return {
                    type: 'icon',
                };
            },

            avatarSize() {
                return (this.isMobile || this.isTablet) ? 4 : 6.25;
            },

            hasDescription() {
                return isString(this.serviceProvider.description) &&
                    !isEmpty(this.serviceProvider.description.trim());
            },

            subtitle() {
                if (this.variant === 'detailed') {
                    return this.serviceProvider.type.professionalTitle;
                }

                return this.serviceProvider.name;
            },

            displayOrganizationName() {
                return (
                    this.serviceProvider.hasContact &&
                    isEmpty(this.organizationLogo)
                ) || (
                    this.serviceProvider.hasContact &&
                    (
                        isEmpty(this.serviceProvider.contactAvatar) ||
                    isEmpty(this.serviceProvider.contactAvatar.publicStorageUrl)
                    )
                );
            },

            displayOrganizationLogo() {
                return this.serviceProvider.hasContact &&
                    !isEmpty(this.serviceProvider.contactAvatar) &&
                    !isEmpty(this.serviceProvider.contactAvatar.publicStorageUrl) &&
                    !isEmpty(this.organizationLogo);
            },

            organizationLogo() {
                if (
                    !isEmpty(this.serviceProvider.organizationLogo) &&
                    !isEmpty(this.serviceProvider.organizationLogo.publicStorageUrl)
                ) {
                    return {
                        src: this.serviceProvider.organizationLogo.publicStorageUrl,
                        name: this.serviceProvider.name,
                    };
                }

                return null;
            },
        },

        methods: {
            addProtocol,
            shortenUrl,

            handleClick() {
                this.$emit('select', this.serviceProvider);
            }
        },
    };
</script>
